import { useMemo } from "react"
import Select, { type SelectTriggerProps } from "@/v2-ui/select"
import cn from "@/v2-ui/utils/utils.cn"
import { useTableContext } from "./table.context"
import { getColumnTitle, canColumnGroup } from "./table.utils"

const NO_GROUPING = "__none__"

export type TableGroupSelectorProps = SelectTriggerProps & {
  placeholder?: string
}

/**
 * Grouping dropdown - reads groupable columns from table.
 */
function TableGroupSelector(props: TableGroupSelectorProps) {
  const {
    placeholder = "Group by...",
    className,
    ...selectTriggerProps
  } = props

  const { table } = useTableContext()
  const groupableColumns = useMemo(
    () => table.getAllColumns().filter(canColumnGroup),
    [table],
  )

  const { grouping } = table.getState()
  const currentValue = grouping.length > 0 ? grouping[0] : NO_GROUPING

  if (groupableColumns.length === 0) return null

  return (
    <Select
      value={currentValue}
      onValueChange={(columnId) => {
        table.setGrouping(columnId === NO_GROUPING ? [] : [columnId])
      }}
    >
      <Select.Trigger
        className={cn("ui-table-group-selector", className)}
        size="xs"
        {...selectTriggerProps}
      >
        <Select.Value placeholder={placeholder} />
      </Select.Trigger>

      <Select.Content>
        <Select.Item value={NO_GROUPING}>No grouping</Select.Item>
        <Select.Separator />
        {groupableColumns.map((column) => (
          <Select.Item key={column.id} value={column.id}>
            {getColumnTitle(column)}
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  )
}

export default TableGroupSelector
